/**
 * AutoCount - Shipments List Page
 * Inbound shipments for the user's warehouse, from the API.
 */

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import { listShipments, ShipmentSummary, ApiError } from "api";

const STATUS_COLORS: Record<
  ShipmentSummary["status"],
  "success" | "info" | "warning" | "secondary" | "error"
> = {
  COMPLETED: "success",
  IN_TRANSIT: "info",
  RECEIVING: "warning",
  PENDING: "secondary",
  DISCREPANCY: "error",
  CANCELLED: "error",
};

const formatStatus = (status: string): string =>
  status
    .split("_")
    .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
    .join(" ");

const formatDate = (value: string | null): string =>
  value
    ? new Date(value).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "—";

function ShipmentsList(): JSX.Element {
  const navigate = useNavigate();
  const [shipments, setShipments] = useState<ShipmentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listShipments({ pageSize: 100 })
      .then((response) => {
        if (cancelled) return;
        setShipments(response.data);
        setLoading(false);
      })
      .catch((err: ApiError) => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const columns = useMemo(
    () => [
      { Header: "Shipment #", accessor: "shipmentNumber", width: "15%" },
      { Header: "Supplier", accessor: "supplier", width: "20%" },
      { Header: "Status", accessor: "status", width: "13%" },
      { Header: "Items", accessor: "items", width: "12%" },
      { Header: "Expected", accessor: "expected", width: "13%" },
      { Header: "Received", accessor: "received", width: "13%" },
      { Header: "Notes", accessor: "notes", width: "14%" },
    ],
    []
  );

  const rows = useMemo(
    () =>
      shipments.map((shipment) => ({
        shipmentNumber: (
          <MDTypography variant="caption" color="info" fontWeight="medium">
            {shipment.shipmentNumber}
          </MDTypography>
        ),
        supplier: shipment.supplierName || "—",
        status: (
          <MDTypography
            variant="caption"
            color={STATUS_COLORS[shipment.status] || "secondary"}
            fontWeight="medium"
          >
            {formatStatus(shipment.status)}
          </MDTypography>
        ),
        items: `${shipment.receivedItems} / ${shipment.totalItems}`,
        expected: formatDate(shipment.expectedDate),
        received: formatDate(shipment.receivedDate),
        notes: shipment.notes || "—",
      })),
    [shipments]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Shipments
                </MDTypography>
                <MDButton
                  variant="gradient"
                  color="dark"
                  size="small"
                  onClick={() => navigate("/shipments/create")}
                >
                  <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                  &nbsp;New Shipment
                </MDButton>
              </MDBox>
              <MDBox pt={3}>
                {error && (
                  <MDBox px={3} pb={2}>
                    <Alert severity="error">{error}</Alert>
                  </MDBox>
                )}
                {loading ? (
                  <MDBox display="flex" justifyContent="center" py={6}>
                    <CircularProgress color="info" />
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={true}
                    entriesPerPage={{ defaultValue: 10, entries: [5, 10, 25, 50] }}
                    showTotalEntries={true}
                    canSearch={true}
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default ShipmentsList;
